<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="modelValue" class="pointer-events-none fixed inset-0 z-50 flex items-end justify-end p-4 sm:p-6">
        <!-- Floating Picture-in-Picture Widget (Non-blocking, live view of table behind) -->
        <div
          class="pointer-events-auto relative w-full transition-all duration-300 flex flex-col rounded-xl shadow-2xl border overflow-hidden"
          :class="[
            isMinimized ? 'max-w-xs' : 'max-w-md md:max-w-lg',
            isDark ? 'bg-gray-900/95 border-gray-700/80 text-white backdrop-blur-md' : 'bg-white/95 border-slate-300 text-slate-900 backdrop-blur-md'
          ]"
        >
          <!-- Header -->
          <header class="flex items-center justify-between px-4 py-3 border-b select-none" :class="isDark ? 'border-gray-800 bg-gray-950/80' : 'border-slate-200 bg-slate-100/80'">
            <div class="flex items-center gap-2.5">
              <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-accent-yellow/20 text-accent-yellow">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="truncate">
                <h3 class="text-xs font-bold truncate">{{ title }}</h3>
                <p class="text-[10px] text-gray-400">Live Non-Blocking Test Runner</p>
              </div>
            </div>

            <div class="flex items-center gap-1">
              <button
                type="button"
                @click="isMinimized = !isMinimized"
                class="rounded-lg p-1 text-gray-400 hover:text-white hover:bg-gray-800 transition"
                :title="isMinimized ? 'Expand view' : 'Minimize view'"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="isMinimized ? 'M4 8h16M4 16h16' : 'M19 9l-7 7-7-7'" />
                </svg>
              </button>
              <button
                type="button"
                @click="close"
                class="rounded-lg p-1 text-gray-400 hover:text-white hover:bg-gray-800 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 011.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </header>

          <!-- Control Bar -->
          <div class="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b text-[11px]" :class="isDark ? 'border-gray-800 bg-gray-900/40' : 'border-slate-200 bg-slate-100/50'">
            <div class="flex items-center gap-1.5">
              <button
                v-if="!isRunning"
                @click="$emit('run')"
                class="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 font-semibold text-white hover:bg-emerald-500 transition shadow"
              >
                ▶ Run Test
              </button>
              <button
                v-else
                @click="isPaused = !isPaused"
                class="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 font-semibold text-white transition shadow"
                :class="isPaused ? 'bg-amber-600 hover:bg-amber-500' : 'bg-gray-700 hover:bg-gray-600'"
              >
                {{ isPaused ? '▶ Resume' : '⏸ Pause' }}
              </button>

              <button
                @click="$emit('reset')"
                :disabled="isRunning"
                class="rounded-lg border px-2.5 py-1.5 text-gray-300 hover:bg-gray-800 transition disabled:opacity-50"
                :class="isDark ? 'border-gray-700' : 'border-slate-300 text-slate-700'"
              >
                🔄 Reset
              </button>
            </div>

            <!-- Speed controls -->
            <div v-if="!isMinimized" class="flex items-center gap-1">
              <button
                v-for="s in speedOptions"
                :key="s.ms"
                @click="speedMs = s.ms"
                class="rounded-md px-2 py-0.5 text-[10px] transition font-medium"
                :class="speedMs === s.ms ? 'bg-accent-yellow text-black font-bold' : isDark ? 'bg-gray-800 text-gray-400 hover:text-white' : 'bg-slate-200 text-slate-600'"
              >
                {{ s.label }}
              </button>
            </div>

            <!-- Stats -->
            <div class="flex items-center gap-2 font-mono text-[10px]">
              <span class="text-emerald-400">✓ {{ passedCount }}</span>
              <span class="text-red-400">✗ {{ failedCount }}</span>
            </div>
          </div>

          <!-- Body content (collapsible) -->
          <div v-if="!isMinimized" class="flex flex-col max-h-[360px] overflow-y-auto p-3 space-y-3">
            <!-- Steps -->
            <div class="space-y-2">
              <div
                v-for="(step, idx) in steps"
                :key="step.id"
                class="rounded-xl border p-2.5 transition-all duration-200"
                :class="[
                  currentStepIndex === idx ? 'ring-2 ring-accent-yellow' : '',
                  step.status === 'passed' ? (isDark ? 'bg-emerald-950/20 border-emerald-800/50' : 'bg-emerald-50 border-emerald-200') :
                  step.status === 'failed' ? (isDark ? 'bg-red-950/20 border-red-800/50' : 'bg-red-50 border-red-200') :
                  step.status === 'running' ? (isDark ? 'bg-amber-950/30 border-amber-600' : 'bg-amber-50 border-amber-300') :
                  (isDark ? 'bg-gray-850 border-gray-800 text-gray-400' : 'bg-slate-50 border-slate-200 text-slate-500')
                ]"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span
                      class="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold"
                      :class="[
                        step.status === 'passed' ? 'bg-emerald-500 text-white' :
                        step.status === 'failed' ? 'bg-red-500 text-white' :
                        step.status === 'running' ? 'bg-amber-500 text-black animate-pulse' :
                        (isDark ? 'bg-gray-800 text-gray-400' : 'bg-slate-200 text-slate-600')
                      ]"
                    >
                      {{ step.status === 'passed' ? '✓' : step.status === 'failed' ? '✗' : idx + 1 }}
                    </span>
                    <span class="font-semibold text-xs truncate" :class="isDark ? 'text-gray-100' : 'text-slate-800'">{{ step.name }}</span>
                  </div>
                  <span v-if="step.durationMs" class="text-[10px] text-gray-400 font-mono">{{ step.durationMs }}ms</span>
                </div>

                <p class="mt-1 text-[11px] text-gray-400 pl-7 leading-tight">{{ step.description }}</p>
                <div v-if="step.error" class="mt-1 pl-7 text-[11px] text-red-400 font-mono">
                  Error: {{ step.error }}
                </div>
              </div>
            </div>

            <!-- Console output stream -->
            <div class="mt-2">
              <h4 class="text-[10px] uppercase font-bold text-gray-400 mb-1">Live Logs</h4>
              <div
                class="rounded-xl p-2.5 font-mono text-[10px] overflow-y-auto space-y-0.5 h-24"
                :class="isDark ? 'bg-black/80 border border-gray-800 text-green-400' : 'bg-slate-900 border border-slate-700 text-green-300'"
              >
                <div v-if="logs.length === 0" class="text-gray-500 italic">Ready. Click 'Run Test'.</div>
                <div v-for="(log, lIdx) in logs" :key="lIdx" class="leading-tight whitespace-pre-wrap">{{ log }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useTheme } from "../composables/useTheme";
import type { TestStep } from "../composables/useInteractiveTest";

const { isDark } = useTheme();

const isMinimized = ref(false);

const props = defineProps<{
  modelValue: boolean;
  title: string;
  steps: TestStep[];
  currentStepIndex: number;
  isRunning: boolean;
  isPaused: boolean;
  logs: string[];
  passedCount: number;
  failedCount: number;
  totalDurationMs: number;
  speedMs: number;
}>();

const emit = defineEmits(["update:modelValue", "update:speedMs", "update:isPaused", "run", "reset"]);

const speedMs = computed({
  get: () => props.speedMs,
  set: (val) => emit("update:speedMs", val),
});

const isPaused = computed({
  get: () => props.isPaused,
  set: (val) => emit("update:isPaused", val),
});

const speedOptions = [
  { label: "Slow (1.2s)", ms: 1200 },
  { label: "Normal (0.6s)", ms: 600 },
  { label: "Fast (0.1s)", ms: 100 },
];

const close = () => {
  emit("update:modelValue", false);
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
